import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import RadioGroup from 'ext-ui/components/RadioGroup/RadioGroup'
import React, { useEffect, useState } from 'react'
import ReceiptAgentAvatar from 'src/components/ReceiptAgentAvatar/ReceiptAgentAvatar'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import { useTranslate } from 'src/localization/Trns'
import { useNullSafeLocale } from 'src/localization/useNullSafeLocale'
import {
  ReceiptRecoveryDisplayCase,
  ReceiptRecoveryLifecycleState,
} from 'src/models/transaction.models'

import {
  getNextReceiptRecoveryMockPreviewState,
  getPreviousReceiptRecoveryMockPreviewState,
  ReceiptRecoveryMockPreviewState,
} from '../receiptRecoveryPreview'
import { getReceiptRecoveryProvenance } from '../receiptRecoveryProvenance'
import style, {
  receiptRecoveryHeaderStyle,
} from './ReceiptRecoverySection.style'

export type ReceiptRecoveryReviewOutcome = 'MATCHES' | 'DOES_NOT_MATCH'

interface ReceiptRecoverySectionProps {
  receiptRecoveryCase: ReceiptRecoveryDisplayCase
  attachmentList?: React.ReactNode
  renderUploader: (isMissing: boolean) => React.ReactNode
  headerSupplement?: React.ReactNode
  headerAction?: React.ReactNode
  isMockPreview?: boolean
  mockPreviewState?: ReceiptRecoveryMockPreviewState
  onMockPreviewStateChange?: (state: ReceiptRecoveryMockPreviewState) => void
  onSubmitReview?: (outcome: ReceiptRecoveryReviewOutcome) => void
  isSubmittingReview?: boolean
  selectedAttachmentId?: string
}

function toDate(timestamp?: string): Date | undefined {
  if (!timestamp) return undefined

  const date = new Date(timestamp)
  return Number.isNaN(date.valueOf()) ? undefined : date
}

function formatShortDate(
  timestamp: string | undefined,
  locale: string,
): string | undefined {
  const date = toDate(timestamp)
  if (!date) return undefined

  return new Intl.DateTimeFormat(locale, {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  }).format(date)
}

function formatDateTime(
  timestamp: string | undefined,
  locale: string,
): string | undefined {
  const date = toDate(timestamp)
  if (!date) return undefined

  return new Intl.DateTimeFormat(locale, {
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

const ReceiptRecoverySection: React.FC<ReceiptRecoverySectionProps> = ({
  receiptRecoveryCase,
  attachmentList,
  renderUploader,
  headerSupplement,
  headerAction,
  isMockPreview = false,
  mockPreviewState,
  onMockPreviewStateChange,
  onSubmitReview,
  isSubmittingReview = false,
  selectedAttachmentId,
}): React.ReactElement => {
  const translate = useTranslate()
  const locale = useNullSafeLocale()
  const [selectedReview, setSelectedReview] =
    useState<ReceiptRecoveryReviewOutcome | null>(null)

  const { lifecycleState } = receiptRecoveryCase
  const recoveredAttachmentId =
    receiptRecoveryCase.recoveredAttachmentId ??
    receiptRecoveryCase.receiptAttachmentId
  const lastAgentRunShortDate = formatShortDate(
    receiptRecoveryCase.lastAgentRunAt,
    locale,
  )
  const recoveredAt =
    receiptRecoveryCase.recoveredReceipt?.submittedAt ??
    receiptRecoveryCase.lastAgentRunAt
  const recoveredAtDateTime = formatDateTime(recoveredAt, locale)
  const reconciledAt = toDate(receiptRecoveryCase.reconciledAt)
  const recoveredReceiptProvenance = getReceiptRecoveryProvenance(
    receiptRecoveryCase.recoveredFrom,
  )
  const reconciledBy =
    receiptRecoveryCase.reconciledByDisplayName ?? translate('a reviewer')
  const shouldShowLifecycleStatus =
    lifecycleState === ReceiptRecoveryLifecycleState.ReceiptReconciled
      ? recoveredAttachmentId !== undefined &&
        selectedAttachmentId === recoveredAttachmentId
      : true

  useEffect(() => {
    setSelectedReview(null)
  }, [lifecycleState, recoveredAttachmentId])

  const renderAgentProvenance = (
    provenance: React.ReactNode,
  ): React.ReactElement => (
    <div className={'ReceiptRecoverySection--agent-provenance'}>
      <ReceiptAgentAvatar className={'ReceiptRecoverySection--agent-avatar'} />
      <span className={'ReceiptRecoverySection--agent-label'}>
        {translate('Receipt Agent')}
      </span>
      <span className={'ReceiptRecoverySection--agent-meta'}>{provenance}</span>
    </div>
  )

  const renderAgentContext = (
    provenance: React.ReactNode,
    description: React.ReactNode,
  ): React.ReactElement => (
    <div className={'ReceiptRecoverySection--agent-context'}>
      {renderAgentProvenance(provenance)}
      <p className={'ReceiptRecoverySection--description'}>{description}</p>
    </div>
  )

  const renderNeedsReview = (): React.ReactElement => (
    <>
      {renderAgentContext(
        recoveredAtDateTime
          ? translate('· recovered ${date}', { date: recoveredAtDateTime })
          : translate('· recovered the document'),
        translate(recoveredReceiptProvenance.description),
      )}
      {attachmentList}
      <div className={'ReceiptRecoverySection--review'}>
        <RadioGroup<ReceiptRecoveryReviewOutcome>
          className={'ReceiptRecoverySection--review-options'}
          name={'receipt-recovery-review'}
          label={translate('Does this receipt match the transaction?')}
          value={selectedReview}
          onChange={(review) => setSelectedReview(review)}
          options={[
            {
              value: 'MATCHES',
              title: translate('Yes, it matches'),
              description: translate('Attach the receipt'),
            },
            {
              value: 'DOES_NOT_MATCH',
              title: translate('No, it doesn’t match'),
              description: translate(
                'Reject the receipt and continue searching.',
              ),
            },
          ]}
        />
        <div className={'ReceiptRecoverySection--submit-row'}>
          <p className={'ReceiptRecoverySection--submit-note'}>
            {translate('Recorded in Activity as your review.')}
          </p>
          <Button
            className={'ReceiptRecoverySection--submit'}
            loading={isSubmittingReview}
            aria-busy={isSubmittingReview}
            disabled={
              isSubmittingReview ||
              selectedReview === null ||
              onSubmitReview === undefined
            }
            onClick={() => selectedReview && onSubmitReview?.(selectedReview)}
          >
            {translate('Submit')}
          </Button>
        </div>
      </div>
    </>
  )

  const renderReconciledOutcome = (): React.ReactElement => (
    <>
      <div
        className={
          'ReceiptRecoverySection--status-row ReceiptRecoverySection--status-row-compact'
        }
      >
        <Pill
          size={'medium'}
          color={
            lifecyclePillColor[ReceiptRecoveryLifecycleState.ReceiptReconciled]
          }
        >
          {lifecycleLabel[ReceiptRecoveryLifecycleState.ReceiptReconciled]}
        </Pill>
        <span className={'ReceiptRecoverySection--agent-meta'}>
          {translate('· Confirmed by ${name}${date}', {
            name: reconciledBy,
            date: reconciledAt
              ? ` · ${formatShortDate(receiptRecoveryCase.reconciledAt, locale)}`
              : '',
          })}
        </span>
      </div>
    </>
  )

  const renderWaitingOnSpender = (): React.ReactElement => (
    <div className={'ReceiptRecoverySection--case'}>
      {renderLifecycleStatus()}
      <p className={'ReceiptRecoverySection--description'}>
        {translate(
          'Receipt request sent. The agent is waiting for the spender’s response — no review is needed from you right now.',
        )}
      </p>
    </div>
  )

  const lifecycleLabel: Record<ReceiptRecoveryLifecycleState, string> = {
    [ReceiptRecoveryLifecycleState.WaitingOnSpender]:
      translate('Waiting on Spender'),
    [ReceiptRecoveryLifecycleState.MissingReceipt]: translate(
      'Receipt Unrecovered',
    ),
    [ReceiptRecoveryLifecycleState.NeedsReview]: translate('Needs Review'),
    [ReceiptRecoveryLifecycleState.ReceiptReconciled]:
      translate('Receipt Reconciled'),
  }

  const lifecyclePillColor: Record<
    ReceiptRecoveryLifecycleState,
    'highlight' | 'pending' | 'severe' | undefined
  > = {
    [ReceiptRecoveryLifecycleState.WaitingOnSpender]: undefined,
    [ReceiptRecoveryLifecycleState.MissingReceipt]: 'severe',
    [ReceiptRecoveryLifecycleState.NeedsReview]: 'pending',
    [ReceiptRecoveryLifecycleState.ReceiptReconciled]: 'highlight',
  }

  const renderMockPreviewControls = (): React.ReactNode => {
    if (!isMockPreview || !mockPreviewState || !onMockPreviewStateChange) {
      return undefined
    }

    return (
      <div className={'ReceiptRecoverySection--mock-preview-controls'}>
        <div className={'ReceiptRecoverySection--mock-preview-navigation'}>
          <button
            type={'button'}
            className={'ReceiptRecoverySection--mock-preview-button'}
            aria-label={translate(
              'Show previous receipt recovery preview state',
            )}
            onClick={() =>
              onMockPreviewStateChange(
                getPreviousReceiptRecoveryMockPreviewState(mockPreviewState),
              )
            }
          >
            <SvgChevronLeft viewBox={'0 0 24 24'} />
          </button>
          <button
            type={'button'}
            className={'ReceiptRecoverySection--mock-preview-button'}
            aria-label={translate('Show next receipt recovery preview state')}
            onClick={() =>
              onMockPreviewStateChange(
                getNextReceiptRecoveryMockPreviewState(mockPreviewState),
              )
            }
          >
            <SvgChevronRight viewBox={'0 0 24 24'} />
          </button>
        </div>
      </div>
    )
  }

  const renderLifecycleStatus = (): React.ReactElement => (
    <div
      className={`ReceiptRecoverySection--status-row${
        lifecycleState === ReceiptRecoveryLifecycleState.WaitingOnSpender
          ? ' ReceiptRecoverySection--status-row-waiting'
          : ''
      }`}
    >
      <Pill size={'medium'} color={lifecyclePillColor[lifecycleState]}>
        {lifecycleLabel[lifecycleState]}
      </Pill>
      {lifecycleState === ReceiptRecoveryLifecycleState.WaitingOnSpender && (
        <span className={'ReceiptRecoverySection--agent-meta'}>
          {lastAgentRunShortDate
            ? translate('last checked ${date}', {
                date: lastAgentRunShortDate,
              })
            : translate('waiting for the spender')}
        </span>
      )}
    </div>
  )

  return (
    <div css={style} className={'ReceiptRecoverySection'}>
      <div css={receiptRecoveryHeaderStyle}>
        <div className={'ReceiptRecoverySection--header-label'}>
          <span className={'font-h3'}>{translate('Receipts')}</span>
          {headerSupplement}
        </div>
        <div className={'ReceiptRecoverySection--header-actions'}>
          {renderMockPreviewControls()}
          {headerAction}
        </div>
      </div>
      {lifecycleState === ReceiptRecoveryLifecycleState.NeedsReview ? (
        <div className={'ReceiptRecoverySection--case'}>
          {renderLifecycleStatus()}
          {renderNeedsReview()}
        </div>
      ) : lifecycleState === ReceiptRecoveryLifecycleState.WaitingOnSpender ? (
        <>
          {renderWaitingOnSpender()}
          {attachmentList}
        </>
      ) : (
        <>
          {shouldShowLifecycleStatus &&
            (lifecycleState === ReceiptRecoveryLifecycleState.ReceiptReconciled
              ? renderReconciledOutcome()
              : renderLifecycleStatus())}
          {attachmentList}
        </>
      )}
      {renderUploader(
        lifecycleState === ReceiptRecoveryLifecycleState.WaitingOnSpender ||
          lifecycleState === ReceiptRecoveryLifecycleState.MissingReceipt,
      )}
    </div>
  )
}

export default ReceiptRecoverySection
